/**
 * Stone Investment Imagine Space Button
 *
 * CTA button for AI Spaces with Stone Investment's luxury aesthetic.
 * Always navigates to the portal Room Designer.
 */

'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Wand2 } from 'lucide-react';

export interface StoneImagineSpaceButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'text';
  size?: 'sm' | 'md' | 'lg';
  /**
   * @deprecated The modal was removed. This prop is kept for backwards
   * compatibility but has no effect -- the button always navigates.
   */
  openModal?: boolean;
  href?: string;
  text?: string;
  showIcon?: boolean;
  locale?: string;
  className?: string;
}

export function StoneImagineSpaceButton({
  variant = 'primary',
  size = 'md',
  href,
  text = 'Imagine Space',
  showIcon = true,
  locale: localeProp,
  className = '',
}: StoneImagineSpaceButtonProps) {
  const pathname = usePathname();

  const match = pathname.match(/^\/([a-z]{2})(\/|$)/);
  const locale = localeProp || match?.[1] || 'en';

  const sizeClasses = {
    sm: 'px-4 py-2 text-xs gap-1.5',
    md: 'px-6 py-3 text-sm gap-2',
    lg: 'px-8 py-4 text-base gap-3',
  };

  const variantClasses = {
    primary: 'bg-stone-900 text-white hover:bg-stone-800',
    secondary: 'bg-stone-100 text-stone-900 hover:bg-stone-200',
    outline:
      'border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50',
    text: 'text-stone-700 hover:text-stone-900 underline',
  };

  const iconSizes = { sm: 14, md: 16, lg: 18 };

  const buttonClasses = `
    inline-flex items-center justify-center font-sans uppercase tracking-[0.15em] transition-colors
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${className}
  `.trim();

  const linkHref = href || `/${locale}/account/spaces`;

  return (
    <Link href={linkHref} className={buttonClasses}>
      {showIcon && <Wand2 size={iconSizes[size]} strokeWidth={1.5} />}
      <span>{text}</span>
    </Link>
  );
}

export default StoneImagineSpaceButton;
